import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Smartphone, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, ASSETS } from "@/lib/assets";

const LINKS = {
  Product: [
    { label: "Meet Fifi", href: "#meet-fifi" },
    { label: "Quant Agent", href: "#fifi-quant" },
    { label: "PawFolio Robo-Advisory", href: "#pawfolio" },
    { label: "Simulation", href: "#simulation" },
    { label: "Master Investors", href: "#masters" },
    { label: "Rewards", href: "#fifi-store" },
  ],
  Company: [
    { label: "About", href: "https://finxstocks.com/ai", external: true },
    { label: "Contact", href: "mailto:info@finxstocks.com", external: true },
  ],
  Legal: [
    { label: "Legal & Disclosures", href: "/legal" },
    { label: "Privacy", href: "/legal/privacy.pdf", external: true },
    { label: "Terms", href: "/legal/terms.pdf", external: true },
    { label: "Cookie Policy", href: "/cookie-policy" },
    { label: "Cookie Preferences", href: "#", onClick: () => window.dispatchEvent(new CustomEvent("finx-open-cookie-preferences")) },
    { label: "Do Not Sell or Share My Personal Information", href: "#", onClick: () => window.dispatchEvent(new CustomEvent("finx-do-not-sell")) },
  ],
};

const FinalCTA = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email, source: "footer" });
      setJoined(true);
      toast.success(`You're #${res.data.total_signups} on the FinX waitlist!`);
    } catch {
      toast.error("Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="final-cta"
      className="relative py-24 overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div className="relative rounded-[2.5rem] brand-gradient p-10 md:p-16 text-center overflow-hidden">
          <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
            backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px, 30px 30px'
          }} />
          <img
            src={ASSETS.fifiSleepy}
            alt=""
            className="absolute -bottom-6 -right-4 w-44 md:w-56 opacity-90 rotate-6"
          />
          <div className="relative z-10">
            <img
              src="/assets/brand/slogan.png"
              alt="Go FinX. Go Alpha."
              data-testid="footer-slogan"
              className="mx-auto w-full max-w-xl md:max-w-2xl h-auto"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <p className="mt-5 text-white/90 max-w-xl mx-auto text-lg">
              Come test with Fifi — learn investing the smart, gamified way.
            </p>
            <form
              data-testid="footer-waitlist-form"
              onSubmit={submit}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
            >
              <Input
                data-testid="footer-waitlist-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={joined}
                className="h-14 rounded-full pl-6 bg-white/95 border-0 text-base"
              />
              <Button
                data-testid="footer-waitlist-submit"
                type="submit"
                disabled={loading || joined}
                className="h-14 rounded-full px-7 font-bold bg-slate-900 hover:bg-black text-white border-0 disabled:opacity-70"
              >
                {joined ? "You're in!" : loading ? "Joining..." : "Join Waitlist"}
                {!joined && !loading && <ArrowRight size={18} className="ml-1.5" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <>
      <FinalCTA />
      <footer data-testid="footer" className="bg-white border-t border-slate-100 pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-12 gap-10">
            {/* Brand column */}
            <div className="md:col-span-4">
              <div className="flex items-center gap-2.5">
                <img src={ASSETS.pawIcon} alt="FinX paw" className="h-9 w-9 object-contain" />
                <img src={ASSETS.wordmark} alt="FinX" className="h-7 w-auto object-contain" />
              </div>
              <p className="mt-4 text-sm text-slate-600 max-w-xs leading-relaxed">
                The Duolingo of Trading. AI quant agents + a gamified avatar that grows with you.
              </p>

              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                  Get the App
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    data-testid="footer-appstore"
                    href="https://apps.apple.com/us/app/finx-stocks/id6783580699"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download FinX on the App Store"
                    className="inline-block hover:-translate-y-0.5 transition-transform"
                  >
                    <img
                      src="/assets/brand/appstore-badge.svg"
                      alt="Download on the App Store"
                      className="h-12 w-auto"
                    />
                  </a>
                  <span
                    data-testid="footer-googleplay"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500"
                  >
                    <Smartphone size={14} />
                    Google Play — coming soon
                  </span>
                </div>
              </div>
            </div>

            {/* Link columns */}
            <div className="md:col-span-8 grid grid-cols-3 gap-6">
              {Object.entries(LINKS).map(([heading, items]) => (
                <div key={heading}>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
                    {heading}
                  </div>
                  <ul className="space-y-2.5">
                    {items.map((l) => (
                      <li key={l.label}>
                        {l.onClick ? (
                          <button
                            data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={(e) => { e.preventDefault(); l.onClick(); }}
                            className="text-sm font-semibold text-slate-700 hover:text-[#5B21B6] text-left"
                          >
                            {l.label}
                          </button>
                        ) : (
                          <a
                            data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            href={l.href}
                            target={l.external ? "_blank" : undefined}
                            rel={l.external ? "noopener noreferrer" : undefined}
                            className="text-sm font-semibold text-slate-700 hover:text-[#5B21B6]"
                          >
                            {l.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-100 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="text-xs text-slate-500">
              © 2026 Golden State Tech. All rights reserved.
            </div>
            <div className="flex items-center gap-4">
              <a
                data-testid="social-reddit"
                href="https://www.reddit.com/r/BuildwithFinX/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Reddit"
                className="text-slate-400 hover:text-[#5B21B6]"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.01 14.92c.04.17.06.34.06.52 0 2.66-3.16 4.81-7.07 4.81s-7.07-2.15-7.07-4.81c0-.18.02-.35.06-.52a1.74 1.74 0 1 1 2.04-2.34c1.18-.81 2.79-1.31 4.56-1.36l.95-4.49a.34.34 0 0 1 .41-.26l3.16.68a1.22 1.22 0 1 1-.13.55l-2.85-.61-.85 4.02c1.79.03 3.42.54 4.6 1.36a1.73 1.73 0 1 1 2.13 2.45zm-9.42-.05a1.18 1.18 0 1 1-2.36 0 1.18 1.18 0 0 1 2.36 0zm6.42 0a1.18 1.18 0 1 1 0-2.36 1.18 1.18 0 0 1 0 2.36zm-1.05 2.41a.34.34 0 0 1 .03.48c-.75.85-1.93 1.27-3 1.27s-2.25-.42-3-1.27a.34.34 0 1 1 .51-.45c.6.68 1.6 1.01 2.49 1.01s1.89-.33 2.49-1.01a.34.34 0 0 1 .48-.03z"/>
                </svg>
              </a>
              <a
                data-testid="social-instagram"
                href="https://www.instagram.com/fifioffinx/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-slate-400 hover:text-[#5B21B6]"
              >
                <Instagram size={18} />
              </a>
              <a
                data-testid="social-linkedin"
                href="https://www.linkedin.com/company/finxstocks/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-slate-400 hover:text-[#5B21B6]"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <p data-testid="footer-service-disclosure" className="mt-6 text-[11px] leading-relaxed text-slate-400 max-w-3xl">
            FinX is a financial education and simulation platform. Simulation features use virtual currency and are for learning purposes only. PawFolio is presented here as a separate robo-advisory product overview; this website does not provide access to its advisory functionality. Trading securities involves risk including possible loss of principal. Brokerage services subject to regulatory approval.
          </p>

          <p data-testid="footer-sec-disclosure" className="mt-4 text-[11px] leading-relaxed text-slate-400 max-w-3xl">
            Golden State Tech Inc., doing business as FinX or FinX Stocks (&ldquo;FinX&rdquo;) is not currently registered as an investment adviser with the SEC or any state securities authority. Golden State Tech Inc.&rsquo;s application for registration as an investment adviser with the U.S. Securities and Exchange Commission (&ldquo;SEC&rdquo;) is currently pending. The information available on this website is for general informational and educational purposes only and does not constitute investment, legal, tax, or other professional advice. Nothing on this website is a recommendation that any security, transaction, portfolio, investment strategy, or planning strategy is suitable for any particular person. Additional information regarding Golden State Tech Inc. may be found through the SEC&rsquo;s Investment Adviser Public Disclosure website at{" "}
            <a
              data-testid="footer-iapd-link"
              href="https://adviserinfo.sec.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#5B21B6]"
            >
              adviserinfo.sec.gov
            </a>{" "}
            by searching for &ldquo;Golden State Tech Inc.&rdquo; or CRD No. 344407.
          </p>
        </div>
      </footer>
    </>
  );
};

export default Footer;
